import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Loader2,
  Square,
  Circle,
  Armchair,
  LayoutGrid,
  Coffee,
  DoorOpen,
  Bath,
  Columns,
  Flower2,
  RectangleHorizontal,
  Star,
  Eye,
  EyeOff,
  Grid3X3,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Info,
  ChevronUp,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { useLocation } from "../contexts/LocationContext";
import { useSubscription } from "@/hooks/useSubscription";
import { usePermissions } from "@/hooks/usePermissions";
import UpgradeRequired from "@/components/UpgradeRequired";
import { useTableDrag } from "@/hooks/useTableDrag";
import AlignmentToolbar from "@/components/AlignmentToolbar";
import { getAuthToken } from "../lib/api-client";

interface Room {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  isDefault: boolean;
  hiddenChannels: string[];
  gridSize: number;
  tables: Table[];
  decorativeObjects: DecorativeObject[];
}

// Canales de reserva donde una sala puede estar visible u oculta.
// Los ids deben coincidir con ROOM_CHANNELS del backend (tables/schema.ts).
const ROOM_CHANNELS: { id: string; label: string; hint: string }[] = [
  { id: "dashboard", label: "Dashboard", hint: "reservas manuales del equipo" },
  { id: "widget", label: "Widget de reservas", hint: "reservas online desde la web" },
  { id: "phone", label: "Teléfono (Carmen)", hint: "asistente de voz" },
  { id: "whatsapp", label: "WhatsApp (IA)", hint: "bot de WhatsApp" },
  { id: "mobile_app", label: "App móvil", hint: "clientes desde la app" },
  { id: "google", label: "Reservar con Google", hint: "Google Reserve" },
];

interface Table {
  id: string;
  roomId: string;
  name: string;
  minCapacity: number;
  maxCapacity: number;
  shape: "rectangle" | "circle" | "square";
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  priority: number;
  isActive: boolean;
  room?: { id: string; name: string };
}

// Tamaño estándar (en celdas) al crear una mesa nueva o al cambiar de forma
// en modo creación — evita que el usuario tenga que ajustar width/height a
// mano cada vez. Las mesas ya existentes NUNCA se recalculan con esto.
const STANDARD_TABLE_SIZE: Record<
  Table["shape"],
  { width: number; height: number }
> = {
  square: { width: 2, height: 2 },
  rectangle: { width: 3, height: 2 },
  circle: { width: 2, height: 2 },
};

// Formulario de mesa: minCapacity/maxCapacity aceptan "" mientras el usuario
// edita el campo (evita forzar un 1 al borrar el número). Se coacciona a
// number en el submit (ver saveTable).
interface TableFormState {
  name: string;
  minCapacity: number | "";
  maxCapacity: number | "";
  shape: "rectangle" | "circle" | "square";
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  priority: number;
}

// Tipos de objetos decorativos
type DecorativeType = "bar" | "bathroom" | "door" | "wall" | "plant" | "column";

interface DecorativeObject {
  id: string;
  type: DecorativeType;
  label?: string;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  rotation: number; // 0, 90, 180, 270
}

const DECORATIVE_TYPES: {
  type: DecorativeType;
  label: string;
  icon: string;
  color: string;
}[] = [
  { type: "bar", label: "Barra", icon: "Coffee", color: "bg-amber-600" },
  { type: "bathroom", label: "Baños", icon: "Bath", color: "bg-blue-500" },
  { type: "door", label: "Puerta", icon: "DoorOpen", color: "bg-green-600" },
  {
    type: "wall",
    label: "Muro",
    icon: "RectangleHorizontal",
    color: "bg-gray-700",
  },
  { type: "plant", label: "Planta", icon: "Flower2", color: "bg-emerald-500" },
  { type: "column", label: "Columna", icon: "Columns", color: "bg-stone-500" },
];

// Lienzo fijo del editor: 50x50 celdas, independiente de `Room.gridSize`
// (que sigue existiendo en BD/API pero ya no se usa para pintar el mapa —
// 50 es superconjunto de todos los tamaños de grid que existían antes, así
// que las posiciones/tamaños guardados siguen siendo válidos tal cual).
const CANVAS_CELLS = 50;
// Tamaño de celda DINÁMICO (zoom): estilo CoverManager (rejilla fina,
// mesas pegadas) pero con auto-encuadre + lupa manual. `cellPx` vive en
// `useMapViewport` (estado por superficie de mapa); estas son sus cotas y
// su valor por defecto (sala vacía / antes de la primera medición).
const DEFAULT_CELL_PX = 18;
const MIN_CELL_PX = 8;
const MAX_CELL_PX = 48;
const ZOOM_STEP = 1.25;
// Margen alrededor de las mesas/objetos al auto-encuadrar, en celdas.
const FIT_MARGIN_CELLS = 1.5;
// Recuadro visible (viewport): tamaño FIJO en px (no vh/flex/zoom) para que
// el alto sea siempre determinista — con `65vh`/`flex-1` el alto disponible
// depende del layout ambiente y puede colapsar a algo muy pequeño. ~30
// celdas a la celda por defecto (18px). El ancho se adapta hacia abajo si
// la pantalla es más estrecha que el recuadro (`w-full` + `maxWidth`).
const RECUADRO_PX = 540;
// Umbral (px de pantalla, no celdas) para distinguir un tap/clic de un
// arrastre real de un objeto decorativo — ver `justDraggedObjectIdRef` más
// abajo, evita que soltar tras arrastrar abra el modal de edición.
const DECORATIVE_DRAG_CLICK_THRESHOLD_PX = 4;

interface CellRect {
  positionX: number;
  positionY: number;
  width: number;
  height: number;
}

interface ContentBBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

// Bounding box (en celdas) de todo el contenido de una sala (mesas +
// objetos decorativos). `null` si la sala está vacía → se usa el zoom por
// defecto en vez de intentar encuadrar la nada.
function computeContentBBox(
  tables: CellRect[],
  decorativeObjects: CellRect[],
): ContentBBox | null {
  const items = [...tables, ...decorativeObjects];
  if (items.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const item of items) {
    minX = Math.min(minX, item.positionX);
    minY = Math.min(minY, item.positionY);
    maxX = Math.max(maxX, item.positionX + (item.width || 1));
    maxY = Math.max(maxY, item.positionY + (item.height || 1));
  }
  return { minX, minY, maxX, maxY };
}

function clampNum(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// Redondea una posición (en celdas) a 3 decimales antes de persistirla.
// `positionX/Y` admiten decimales en BD (posicionamiento libre / smart
// guides) — esto NO trunca a entero, solo recorta ruido de coma flotante
// (p.ej. 4.299999999999999 -> 4.3).
function roundPosition(value: number): number {
  return Math.round(value * 1000) / 1000;
}

// Cuántas líneas de texto (de arriba a abajo, por orden de prioridad)
// caben en `boxHeightPx` sin cortarse a la mitad — o cabe la línea entera
// o no se pinta. `fontSizes` es el tamaño (px) de cada línea candidata.
function countFittingLines(boxHeightPx: number, fontSizes: number[]): number {
  const lineHeightFactor = 1.15;
  let used = 0;
  let count = 0;
  for (const fontPx of fontSizes) {
    const lineH = fontPx * lineHeightFactor;
    if (used + lineH > boxHeightPx + 0.5) break;
    used += lineH;
    count++;
  }
  return count;
}

// El NOMBRE de la mesa NUNCA se recorta con "…" en condiciones normales:
// en vez de truncar a una línea, se envuelve en como mucho 2 líneas
// (`line-clamp-2` + `break-words` en el render). Aquí solo se decide el
// tamaño de fuente: se busca, bajando de 1 en 1 desde `idealFontPx` sin
// pasar de `minFontPx` (el mismo clamp que ya se usaba), el mayor tamaño
// con el que el nombre COMPLETO quepa en 2 líneas envueltas — estimando
// caracteres por línea con un ancho medio de carácter de ~0.5×fontPx
// (razonable para la tipografía fina/sin negrita de este lienzo). Si ni al
// tamaño mínimo cabe en 2 líneas, se usa el mínimo igualmente: el
// `line-clamp-2` del render actúa como último recurso con "…" al final de
// la 2ª línea, no como comportamiento normal.
function fitNameToTwoLines(
  name: string,
  boxWidthPx: number,
  idealFontPx: number,
  minFontPx: number,
): { fontPx: number; lines: number } {
  const charsPerLine = (fontPx: number) =>
    Math.max(1, Math.floor(boxWidthPx / (fontPx * 0.5)));
  let fontPx = idealFontPx;
  while (fontPx > minFontPx && name.length > charsPerLine(fontPx) * 2) {
    fontPx -= 1;
  }
  const lines = name.length > charsPerLine(fontPx) ? 2 : 1;
  return { fontPx, lines };
}

/**
 * Viewport de una superficie de mapa: tamaño de celda dinámico (zoom) +
 * auto-encuadre (fit-to-content) + pan (botón central del ratón). El
 * scroll nativo (barras/trackpad) funciona solo con `overflow-auto` en el
 * contenedor; el pan añade además "arrastrar para desplazar" con el botón
 * central — el izquierdo ya está ocupado por el drag de mesas/objetos y el
 * clic en vacío para añadir una mesa.
 */
// Estructura persistida en localStorage por superficie+sala (ver
// `storageKey`, formato `rm-map-view:<superficie>:<roomId>`).
interface SavedMapView {
  cellPx: number;
  scrollLeft: number;
  scrollTop: number;
}

function useMapViewport({
  totalCells,
  recuadroPx,
  defaultCellPx = DEFAULT_CELL_PX,
  minCellPx = MIN_CELL_PX,
  maxCellPx = MAX_CELL_PX,
  marginCells = FIT_MARGIN_CELLS,
  storageKey = null,
  panTouchEnabled = true,
}: {
  totalCells: number;
  recuadroPx: number;
  defaultCellPx?: number;
  minCellPx?: number;
  maxCellPx?: number;
  marginCells?: number;
  // Clave de localStorage (`rm-map-view:<superficie>:<roomId>`) para
  // persistir/restaurar cellPx+scroll. `null` = no persistir (p.ej. sin
  // sala seleccionada todavía).
  storageKey?: string | null;
  // Pan por arrastre táctil (además del scroll nativo, que SIEMPRE
  // funciona solo con `overflow-auto`, con o sin esto). Se desactiva en
  // superficies editables por touch (mesas arrastrables) para no arriesgar
  // conflicto con ese drag — ahí el pan de fondo se queda solo con scroll
  // nativo, como pide el criterio "en modo edición usa scroll nativo".
  panTouchEnabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cellPx, setCellPxRaw] = useState(defaultCellPx);
  // Ref "espejo" del cellPx actual: los closures de guardado (creados una
  // vez por `storageKey`, no por cada cambio de zoom) necesitan leer el
  // valor MÁS RECIENTE en el momento de guardar, no el que tenían cuando
  // se crearon. Se actualiza en cada render (patrón habitual de ref
  // "última versión conocida", no dispara re-render).
  const cellPxRef = useRef(cellPx);
  cellPxRef.current = cellPx;

  const clampCellPx = useCallback(
    (v: number) => clampNum(v, minCellPx, maxCellPx),
    [minCellPx, maxCellPx],
  );

  const zoomIn = useCallback(() => {
    setCellPxRaw((v) => clampCellPx(v * ZOOM_STEP));
  }, [clampCellPx]);

  const zoomOut = useCallback(() => {
    setCellPxRaw((v) => clampCellPx(v / ZOOM_STEP));
  }, [clampCellPx]);

  // Guardado con debounce (400ms) de la vista actual (cellPx + scroll) bajo
  // `storageKey`. Se dispara en cada cambio de `cellPx` (zoom/fit/restore)
  // y en cada evento `scroll` nativo del contenedor (pan con ratón/touch,
  // scroll con rueda/barras). `localStorage` puede fallar (modo privado,
  // cuota) — nunca debe romper el mapa, solo no persiste.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSave = useCallback(() => {
    if (!storageKey) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      try {
        const data: SavedMapView = {
          cellPx: cellPxRef.current,
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop,
        };
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch {
        // localStorage no disponible (privado/cuota) — no persiste, no rompe el mapa
      }
    }, 400);
  }, [storageKey]);

  useEffect(() => {
    scheduleSave();
  }, [cellPx, scheduleSave]);

  const onScroll = useCallback(() => {
    scheduleSave();
  }, [scheduleSave]);

  // Auto-encuadre: calcula `cellPx` a partir del bounding box del contenido
  // (en celdas) y desplaza el scroll para dejarlo visible. Usa el
  // ancho/alto REALES del contenedor (`clientWidth`/`clientHeight`), NO
  // `recuadroPx` a pelo — en viewports estrechos (móvil) el recuadro real
  // es más estrecho que `recuadroPx` (que ya solo actúa como tope máximo
  // vía `maxWidth`), así que dividir por `recuadroPx` ahí sobrestimaba el
  // espacio disponible y dejaba el lienzo con muy poca área realmente
  // visible ("se ve pequeño"). Con fallback a `recuadroPx` solo si el
  // contenedor aún no ha montado (medida 0). El `rAF` es necesario porque
  // el nuevo tamaño del lienzo (ligado a `cellPx`) tarda un ciclo de
  // render + layout en aplicarse antes de que scrollLeft/scrollTop tengan
  // efecto real.
  const fitToContent = useCallback(
    (bbox: ContentBBox | null) => {
      const viewportWidthPx = containerRef.current?.clientWidth || recuadroPx;
      const viewportHeightPx =
        containerRef.current?.clientHeight || recuadroPx;
      if (!bbox) {
        setCellPxRaw(defaultCellPx);
        requestAnimationFrame(() => {
          const el = containerRef.current;
          if (el) {
            el.scrollLeft = 0;
            el.scrollTop = 0;
          }
        });
        return;
      }
      const minX = Math.max(0, bbox.minX - marginCells);
      const minY = Math.max(0, bbox.minY - marginCells);
      const maxX = Math.min(totalCells, bbox.maxX + marginCells);
      const maxY = Math.min(totalCells, bbox.maxY + marginCells);
      const widthCells = Math.max(1, maxX - minX);
      const heightCells = Math.max(1, maxY - minY);
      const next = clampCellPx(
        Math.min(viewportWidthPx / widthCells, viewportHeightPx / heightCells),
      );
      setCellPxRaw(next);
      requestAnimationFrame(() => {
        const el = containerRef.current;
        if (!el) return;
        el.scrollLeft = minX * next;
        el.scrollTop = minY * next;
      });
    },
    [clampCellPx, defaultCellPx, marginCells, recuadroPx, totalCells],
  );

  // Al cambiar de sala: restaura la vista guardada (cellPx + scroll) si
  // existe una para `storageKey`; si no hay nada guardado (o falla el
  // parseo/localStorage), cae al auto-encuadre de siempre.
  const restoreOrFit = useCallback(
    (bbox: ContentBBox | null) => {
      if (storageKey) {
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) {
            const saved = JSON.parse(raw) as Partial<SavedMapView>;
            if (
              typeof saved.cellPx === "number" &&
              typeof saved.scrollLeft === "number" &&
              typeof saved.scrollTop === "number"
            ) {
              const next = clampCellPx(saved.cellPx);
              setCellPxRaw(next);
              requestAnimationFrame(() => {
                const el = containerRef.current;
                if (!el) return;
                el.scrollLeft = saved.scrollLeft as number;
                el.scrollTop = saved.scrollTop as number;
              });
              return;
            }
          }
        } catch {
          // JSON corrupto / localStorage no disponible — se ignora y se hace fit
        }
      }
      fitToContent(bbox);
    },
    [storageKey, clampCellPx, fitToContent],
  );

  // Pan con botón central del ratón (desktop) — scroll nativo (barras/
  // trackpad/rueda) siempre funciona vía `overflow-auto`; esto añade
  // además "arrastrar para desplazar" sin interferir con el drag de
  // mesas/objetos ni el clic para añadir una mesa (ambos en botón
  // izquierdo).
  const panStateRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 1) return; // solo botón central
    const container = containerRef.current;
    if (!container) return;
    e.preventDefault();
    panStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
    setIsPanning(true);
  }, []);

  useEffect(() => {
    if (!isPanning) return;
    const handleMove = (e: MouseEvent) => {
      const state = panStateRef.current;
      const container = containerRef.current;
      if (!state || !container) return;
      container.scrollLeft = state.scrollLeft - (e.clientX - state.startX);
      container.scrollTop = state.scrollTop - (e.clientY - state.startY);
    };
    const handleUp = () => {
      panStateRef.current = null;
      setIsPanning(false);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isPanning]);

  // Pan táctil (opcional, `panTouchEnabled`): el scroll nativo por
  // arrastre de un dedo YA funciona en cualquier `overflow-auto` sin nada
  // de esto — esto es solo para las superficies de solo lectura donde
  // queremos control total del gesto (evita que el scroll nativo y este
  // pan compitan). Umbral de 6px antes de considerar que es un arrastre
  // (no un tap) para no robarle el toque a los clics de seleccionar/abrir
  // mesa. NO se activa en superficies con mesas arrastrables por touch
  // (`panTouchEnabled=false`) para que el único gesto de arrastre posible
  // sea el drag de mesas, sin ambigüedad.
  const touchStateRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
    engaged: boolean;
  } | null>(null);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!panTouchEnabled) return;
      const container = containerRef.current;
      if (!container || e.touches.length !== 1) return;
      const touch = e.touches[0];
      touchStateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
        engaged: false,
      };
    },
    [panTouchEnabled],
  );

  useEffect(() => {
    if (!panTouchEnabled) return;
    const handleTouchMove = (e: TouchEvent) => {
      const state = touchStateRef.current;
      const container = containerRef.current;
      if (!state || !container || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;
      if (!state.engaged) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return; // aún podría ser un tap
        state.engaged = true;
      }
      e.preventDefault(); // ya lo llevamos nosotros: evita que el scroll nativo compita
      container.scrollLeft = state.scrollLeft - dx;
      container.scrollTop = state.scrollTop - dy;
    };
    const handleTouchEnd = () => {
      touchStateRef.current = null;
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);
    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [panTouchEnabled]);

  return {
    containerRef,
    cellPx,
    zoomIn,
    zoomOut,
    fitToContent,
    restoreOrFit,
    onScroll,
    isPanning,
    onMouseDown,
    onTouchStart,
  };
}

// Botonera flotante de zoom (+/-/ajustar), reutilizada en las 4 superficies
// de mapa (editor + mapa en vivo + 3 modales de Reservations.tsx).
function ZoomControls({
  onZoomIn,
  onZoomOut,
  onFit,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
}) {
  // w-11 h-11 = 44px: área táctil mínima recomendada (Apple HIG), holgada
  // también en desktop con el ratón.
  const btnClass =
    "w-11 h-11 flex items-center justify-center bg-white border border-gray-300 rounded-lg shadow-sm text-gray-600 hover:bg-gray-50 active:bg-gray-100 hover:text-gray-900";
  return (
    <div className="absolute top-2 right-2 z-[70] flex flex-col gap-1.5">
      <button
        type="button"
        onClick={onZoomIn}
        className={btnClass}
        title="Acercar"
      >
        <ZoomIn className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onZoomOut}
        className={btnClass}
        title="Alejar"
      >
        <ZoomOut className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onFit}
        className={btnClass}
        title="Ajustar al contenido"
      >
        <Maximize2 className="w-5 h-5" />
      </button>
    </div>
  );
}

// --- Creación en serie de mesas: helpers puros (sin dependencias del
// componente) para "Guardar y siguiente" ---

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

// Incrementa el ÚLTIMO número del nombre conservando los ceros a la
// izquierda ("Mesa 09" -> "Mesa 10", "M-08" -> "M-09"). Si no hay ningún
// número en el nombre, añade " 2" al final.
function incrementTableName(name: string): string {
  const match = name.match(/(\d+)(\D*)$/);
  if (!match || match.index === undefined) {
    return `${name} 2`;
  }
  const [, numStr, suffix] = match;
  const nextNum = (parseInt(numStr, 10) + 1)
    .toString()
    .padStart(numStr.length, "0");
  return `${name.slice(0, match.index)}${nextNum}${suffix}`;
}

// Incrementa en cadena hasta encontrar un nombre que no esté ya en uso.
function nextAvailableTableName(
  baseName: string,
  existingNames: Set<string>,
): string {
  let candidate = incrementTableName(baseName);
  while (existingNames.has(candidate)) {
    candidate = incrementTableName(candidate);
  }
  return candidate;
}

// Escanea el grid (gridSize x gridSize) fila a fila empezando en (startX,
// startY) y, si no encuentra hueco antes de llegar al final, continúa desde
// el origen (recorrido circular). Devuelve la primera posición donde el
// rect width x height no solapa ningún rect ya ocupado (colisión AABB
// estándar). Si no hay ningún hueco libre, devuelve la posición de partida.
function findFreePosition(
  gridSize: number,
  width: number,
  height: number,
  occupied: Rect[],
  startX: number,
  startY: number,
): { x: number; y: number } {
  const maxX = gridSize - width;
  const maxY = gridSize - height;
  if (maxX < 0 || maxY < 0) {
    return { x: startX, y: startY };
  }

  const cells: { x: number; y: number }[] = [];
  for (let y = 0; y <= maxY; y++) {
    for (let x = 0; x <= maxX; x++) {
      cells.push({ x, y });
    }
  }

  const startIndex = cells.findIndex(
    (c) => c.y > startY || (c.y === startY && c.x >= startX),
  );
  const ordered =
    startIndex === -1
      ? cells
      : [...cells.slice(startIndex), ...cells.slice(0, startIndex)];

  for (const cell of ordered) {
    const candidate: Rect = { x: cell.x, y: cell.y, width, height };
    if (!occupied.some((r) => rectsOverlap(candidate, r))) {
      return cell;
    }
  }
  return { x: startX, y: startY };
}

// Extrae un mensaje de error legible del cuerpo JSON de una respuesta
// fallida de la API. El backend adjunta `errors` (issues de Zod, cada uno
// con `.message`) en los 400 de validación junto a un `message` genérico
// ("Datos inválidos"/"Invalid data") — se prioriza el primer issue
// específico (p.ej. "Number must be less than or equal to 10") sobre ese
// genérico; si no hay issues, cae al `message` general; si no hay nada
// reconocible, usa el `fallback`.
function extractApiErrorMessage(data: unknown, fallback: string): string {
  if (data && typeof data === "object") {
    const obj = data as { message?: string; errors?: { message?: string }[] };
    const firstIssue = Array.isArray(obj.errors)
      ? obj.errors.find((e) => typeof e?.message === "string" && e.message)
      : undefined;
    if (firstIssue?.message) return firstIssue.message;
    if (obj.message) return obj.message;
  }
  return fallback;
}

export default function TableMap() {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";
  const navigate = useNavigate();
  const { locationVersion, currentBookingMode } = useLocation();
  const { can } = usePermissions();

  // Gating por permisos (FASE 3c): el backend restringe las mutaciones de
  // mesas/salas según el permiso `mesas:editar` (owner/admin siempre pasan
  // vía `usePermissions`, staff según su configuración). Se replica aquí
  // para deshabilitar la UI proactivamente (mismo patrón que
  // `canEditSchedule` en Schedule.tsx); el 403 del backend queda como red
  // de seguridad si algo se cuela.
  const canEdit = can("mesas:editar");

  // Check subscription access
  const {
    features,
    isLoading: subscriptionLoading,
    canAccessTableMaps,
  } = useSubscription();

  // State
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reorderingRooms, setReorderingRooms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Room modal
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [roomForm, setRoomForm] = useState({
    name: "",
    description: "",
    hiddenChannels: [] as string[],
  });

  // Table modal
  const [showTableModal, setShowTableModal] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [tableForm, setTableForm] = useState<TableFormState>({
    name: "",
    minCapacity: 1,
    maxCapacity: 4,
    shape: "rectangle",
    positionX: 0,
    positionY: 0,
    width: 1,
    height: 1,
    priority: 0,
  });

  // Grid container ref for pointer-based drag
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [snapToGrid, setSnapToGrid] = useState(true);

  // Viewport del lienzo: zoom dinámico (cellPx) + auto-encuadre + pan.
  // `panTouchEnabled: false` — el editor SIEMPRE es editable (mesas y
  // objetos arrastrables por touch), así que el pan de fondo se queda solo
  // con scroll nativo (criterio: "en modo edición usa scroll nativo").
  const viewport = useMapViewport({
    totalCells: CANVAS_CELLS,
    recuadroPx: RECUADRO_PX,
    storageKey: selectedRoom ? `rm-map-view:tables:${selectedRoom.id}` : null,
    panTouchEnabled: false,
  });

  // Decorative objects - stored per room in localStorage
  const [decorativeObjects, setDecorativeObjects] = useState<
    DecorativeObject[]
  >([]);
  const [showDecorativeModal, setShowDecorativeModal] = useState(false);
  const [editingDecorative, setEditingDecorative] =
    useState<DecorativeObject | null>(null);
  const [decorativeForm, setDecorativeForm] = useState<{
    type: DecorativeType;
    label: string;
    positionX: number;
    positionY: number;
    width: number;
    height: number;
    rotation: number;
  }>({
    type: "bar",
    label: "",
    positionX: 0,
    positionY: 0,
    width: 2,
    height: 1,
    rotation: 0,
  });

  // Get auth headers - reads from localStorage each time to get current location
  const getAuthHeaders = useCallback(() => {
    const locationId = localStorage.getItem("rm_current_location_id");
    const organizationId = localStorage.getItem("rm_current_organization_id");
    const token = getAuthToken();
    return {
      "Content-Type": "application/json",
      ...(locationId ? { "X-Location-Id": locationId } : {}),
      ...(organizationId ? { "X-Organization-Id": organizationId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [locationVersion]); // Re-create when location changes

  const authFetch = useCallback(
    (url: string, options: RequestInit = {}) =>
      fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...(options.headers as Record<string, string>),
        },
        credentials: "include",
      }),
    [getAuthHeaders],
  );

  // Ref para evitar dependencia circular en loadRooms
  const selectedRoomIdRef = useRef<string | null>(null);

  // Actualizar ref cuando cambia selectedRoom
  useEffect(() => {
    selectedRoomIdRef.current = selectedRoom?.id || null;
  }, [selectedRoom]);

  // Load rooms
  const loadRooms = useCallback(async () => {
    try {
      setLoadError(null);
      const res = await authFetch(
        `${API_URL}/tables/rooms?includeInactive=true`,
        {},
      );
      if (!res.ok) {
        if (res.status === 401) {
          navigate("/login");
          return;
        }
        const data = await res.json().catch(() => null);
        throw new Error(
          extractApiErrorMessage(data, "Error al cargar las salas"),
        );
      }
      const data = await res.json();
      const loadedRooms = data.rooms || [];
      setRooms(loadedRooms);

      // Si hay una sala seleccionada, actualizarla con los datos nuevos
      const currentRoomId = selectedRoomIdRef.current;
      if (currentRoomId) {
        const updatedRoom = loadedRooms.find(
          (r: Room) => r.id === currentRoomId,
        );
        if (updatedRoom) {
          setSelectedRoom(updatedRoom);
          // Actualizar decorativeObjects desde la sala
          setDecorativeObjects(updatedRoom.decorativeObjects || []);
        }
      } else if (loadedRooms.length > 0) {
        // Si no hay sala seleccionada, seleccionar la primera
        setSelectedRoom(loadedRooms[0]);
        setDecorativeObjects(loadedRooms[0].decorativeObjects || []);
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : "Error al cargar las salas";
      // Estado de ERROR de carga, distinto de "no hay salas" (rooms=[]
      // real): el render usa `loadError` para mostrar un estado con
      // "Reintentar" en vez del vacío feliz "Crear Primera Sala".
      setLoadError(errorMessage);
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders, navigate]);

  // Reload rooms when location changes - reset selected room first
  useEffect(() => {
    setSelectedRoom(null); // Reset so first room of new location is selected
    loadRooms();
  }, [loadRooms, locationVersion]);

  // El lienzo del editor es fijo (CANVAS_CELLS), así que aquí solo hace
  // falta sincronizar los decorativeObjects al cambiar de sala. `Room.gridSize`
  // sigue viajando desde la API pero ya no se lee para pintar el mapa.
  useEffect(() => {
    if (!selectedRoom) return;
    setDecorativeObjects(selectedRoom.decorativeObjects || []);
  }, [selectedRoom]);

  // Al cargar/cambiar de sala: restaura la vista guardada (cellPx+scroll)
  // si existe, si no auto-encuadra. Deliberadamente keyed solo por `.id`
  // (no por `selectedRoom` completo) para NO re-encuadrar/resetear la vista
  // en cada refresco de datos (loadRooms() se llama tras casi cualquier
  // mutación) — eso resetearía el zoom/pan del usuario mientras está
  // editando. El botón "Ajustar" de ZoomControls permite re-encuadrar a
  // mano en cualquier momento (y actualiza lo guardado).
  useEffect(() => {
    if (!selectedRoom) return;
    const bbox = computeContentBBox(
      selectedRoom.tables ?? [],
      selectedRoom.decorativeObjects ?? [],
    );
    viewport.restoreOrFit(bbox);
  }, [selectedRoom?.id, viewport.restoreOrFit]);

  // Clear message after 3s
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Room CRUD
  const openRoomModal = (room?: Room) => {
    if (room) {
      setEditingRoom(room);
      setRoomForm({
        name: room.name,
        description: room.description || "",
        hiddenChannels: room.hiddenChannels || [],
      });
    } else {
      setEditingRoom(null);
      setRoomForm({ name: "", description: "", hiddenChannels: [] });
    }
    setShowRoomModal(true);
  };

  // Tick marcado = visible en ese canal (el form guarda los canales OCULTOS)
  const toggleRoomChannel = (channelId: string) => {
    setRoomForm((prev) => ({
      ...prev,
      hiddenChannels: prev.hiddenChannels.includes(channelId)
        ? prev.hiddenChannels.filter((c) => c !== channelId)
        : [...prev.hiddenChannels, channelId],
    }));
  };

  const saveRoom = async () => {
    if (!roomForm.name.trim()) {
      setMessage({ type: "error", text: "El nombre es requerido" });
      return;
    }

    setSaving(true);
    try {
      const url = editingRoom
        ? `${API_URL}/tables/rooms/${editingRoom.id}`
        : `${API_URL}/tables/rooms`;
      const method = editingRoom ? "PUT" : "POST";

      const res = await authFetch(url, {
        method,
        body: JSON.stringify(roomForm),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Error");
      }

      setMessage({
        type: "success",
        text: editingRoom ? "Sala actualizada" : "Sala creada",
      });
      setShowRoomModal(false);
      loadRooms();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error al guardar";
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const deleteRoom = async (room: Room) => {
    setConfirmDialog({
      show: true,
      title: "Eliminar sala",
      message: `¿Eliminar la sala "${room.name}"? Se eliminarán todas sus mesas.`,
      onConfirm: async () => {
        setConfirmDialog({
          show: false,
          title: "",
          message: "",
          onConfirm: () => {},
        });
        try {
          const res = await authFetch(`${API_URL}/tables/rooms/${room.id}`, {
            method: "DELETE",
          });

          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.message || "Error");
          }

          setMessage({ type: "success", text: "Sala eliminada" });
          if (selectedRoom?.id === room.id) {
            setSelectedRoom(null);
          }
          loadRooms();
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Error al eliminar";
          setMessage({ type: "error", text: errorMessage });
        }
      },
    });
  };

  // Set room as default
  const setRoomAsDefault = async (room: Room) => {
    try {
      const res = await authFetch(
        `${API_URL}/tables/rooms/${room.id}/default`,
        {
          method: "PUT",
        },
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Error");
      }

      const data = await res.json();
      setMessage({
        type: "success",
        text: data.message || "Sala marcada por defecto",
      });
      loadRooms();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error al marcar por defecto";
      setMessage({ type: "error", text: errorMessage });
    }
  };

  // Toggle room visibility (isActive)
  const toggleRoomVisibility = async (room: Room) => {
    try {
      const res = await authFetch(`${API_URL}/tables/rooms/${room.id}`, {
        method: "PUT",
        body: JSON.stringify({ isActive: !room.isActive }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Error");
      }

      setMessage({
        type: "success",
        text: room.isActive
          ? "Sala oculta de todos los canales"
          : "Sala visible en los canales marcados",
      });
      loadRooms();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error al cambiar visibilidad";
      setMessage({ type: "error", text: errorMessage });
    }
  };

  // Reordenar salas: intercambia la sala con su vecina (arriba/abajo),
  // actualiza el array local de forma optimista y envía la lista COMPLETA
  // de ids en el nuevo orden a `PUT /tables/rooms/reorder`
  // (`{ roomIds: string[] }`), tal y como espera `reorderRoomsSchema` del
  // backend. Si falla, se revierte recargando desde el servidor.
  const moveRoom = async (room: Room, direction: "up" | "down") => {
    if (reorderingRooms) return;
    const index = rooms.findIndex((r) => r.id === room.id);
    if (index === -1) return;
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= rooms.length) return;

    const reordered = [...rooms];
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];
    setRooms(reordered); // Optimistic

    setReorderingRooms(true);
    try {
      const res = await authFetch(`${API_URL}/tables/rooms/reorder`, {
        method: "PUT",
        body: JSON.stringify({ roomIds: reordered.map((r) => r.id) }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          extractApiErrorMessage(data, "Error al reordenar las salas"),
        );
      }

      await loadRooms();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error al reordenar las salas";
      setMessage({ type: "error", text: errorMessage });
      await loadRooms(); // Revertir al orden real del servidor
    } finally {
      setReorderingRooms(false);
    }
  };

  // Table CRUD
  const openTableModal = (
    table?: Table,
    position?: { x: number; y: number },
  ) => {
    if (table) {
      setEditingTable(table);
      setTableForm({
        name: table.name,
        minCapacity: table.minCapacity,
        maxCapacity: table.maxCapacity,
        shape: table.shape,
        positionX: table.positionX,
        positionY: table.positionY,
        width: table.width,
        height: table.height,
        priority: table.priority,
      });
    } else {
      setEditingTable(null);
      const defaultShape = "rectangle" as const;
      // El nombre sugerido parte del número de mesa MÁS ALTO existente + 1 (no del
      // recuento: si la numeración tiene huecos —p. ej. 20 mesas numeradas 1..19 y
      // 21— el recuento sugería "Mesa 21", que ya existía) y se incrementa hasta un
      // nombre libre reutilizando la misma lógica que "Guardar y siguiente".
      const roomTables = selectedRoom?.tables ?? [];
      const existingNames = new Set(roomTables.map((t) => t.name));
      const maxNum = roomTables.reduce((mx, t) => {
        const m = t.name.match(/(\d+)\D*$/);
        return m ? Math.max(mx, parseInt(m[1], 10)) : mx;
      }, 0);
      setTableForm({
        name: nextAvailableTableName(`Mesa ${maxNum}`, existingNames),
        minCapacity: 1,
        maxCapacity: 4,
        shape: defaultShape,
        positionX: position?.x || 0,
        positionY: position?.y || 0,
        ...STANDARD_TABLE_SIZE[defaultShape],
        priority: 0,
      });
    }
    setShowTableModal(true);
  };

  // Actualiza minCapacity/maxCapacity permitiendo el valor vacío mientras el
  // usuario edita (no fuerza un 1 en cada pulsación). La validación/coacción
  // a número ocurre en el submit (saveTable).
  const handleCapacityInputChange = (
    field: "minCapacity" | "maxCapacity",
    rawValue: string,
  ) => {
    if (rawValue === "") {
      setTableForm((prev) => ({ ...prev, [field]: "" }));
      return;
    }
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) return;
    setTableForm((prev) => ({ ...prev, [field]: parsed }));
  };

  // `andNext=true` guarda la mesa y, en vez de cerrar el modal, deja listo
  // el formulario para crear la siguiente de la cadena: misma configuración
  // (capacidades, forma, tamaño, prioridad), nombre +1 y la primera
  // posición libre del grid. `editingTable` en ese caso actúa como
  // "duplicar como siguiente" (la edición se guarda igualmente, y a partir
  // de ahí se crea una mesa nueva).
  const saveTable = async (andNext = false) => {
    if (!selectedRoom) return;
    if (saving) return;
    if (!tableForm.name.trim()) {
      setMessage({ type: "error", text: "El nombre es requerido" });
      return;
    }
    if (tableForm.minCapacity === "" || tableForm.maxCapacity === "") {
      setMessage({
        type: "error",
        text: "Indica la capacidad mínima y máxima de la mesa",
      });
      return;
    }

    const minCapacity = tableForm.minCapacity;
    const maxCapacity = tableForm.maxCapacity;

    if (minCapacity < 1 || maxCapacity < 1) {
      setMessage({
        type: "error",
        text: "La capacidad debe ser de al menos 1 persona",
      });
      return;
    }
    if (minCapacity > maxCapacity) {
      setMessage({
        type: "error",
        text: "La capacidad mínima no puede ser mayor que la máxima",
      });
      return;
    }

    setSaving(true);
    try {
      const url = editingTable
        ? `${API_URL}/tables/${editingTable.id}`
        : `${API_URL}/tables`;
      const method = editingTable ? "PUT" : "POST";

      const body = editingTable
        ? { ...tableForm, minCapacity, maxCapacity }
        : {
            ...tableForm,
            roomId: selectedRoom.id,
            minCapacity,
            maxCapacity,
          };

      const res = await authFetch(url, {
        method,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(extractApiErrorMessage(data, "Error al guardar la mesa"));
      }

      if (andNext) {
        // La mesa recién guardada aún no está reflejada en
        // `selectedRoom.tables` (loadRooms es async), así que se añade a
        // mano al set de nombres/rects ocupados antes de calcular la
        // siguiente. Si estábamos editando, se excluye la fila antigua de
        // esa mesa (puede estar en una posición/nombre distintos a los que
        // se acaban de guardar).
        const savedName = tableForm.name;
        const savedRect: Rect = {
          x: tableForm.positionX,
          y: tableForm.positionY,
          width: tableForm.width,
          height: tableForm.height,
        };
        const otherTables = (selectedRoom.tables || []).filter(
          (t) => t.id !== editingTable?.id,
        );
        const existingNames = new Set(
          otherTables.map((t) => t.name).concat(savedName),
        );
        const occupiedRects: Rect[] = otherTables
          .map((t) => ({
            x: t.positionX,
            y: t.positionY,
            width: t.width,
            height: t.height,
          }))
          .concat(savedRect);

        const nextName = nextAvailableTableName(savedName, existingNames);
        const nextPosition = findFreePosition(
          CANVAS_CELLS,
          tableForm.width,
          tableForm.height,
          occupiedRects,
          savedRect.x,
          savedRect.y,
        );

        setEditingTable(null);
        setTableForm({
          ...tableForm,
          name: nextName,
          positionX: nextPosition.x,
          positionY: nextPosition.y,
        });
        setMessage({
          type: "success",
          text: `Mesa «${savedName}» ${editingTable ? "actualizada" : "creada"} — siguiente preparada`,
        });
      } else {
        setMessage({
          type: "success",
          text: editingTable ? "Mesa actualizada" : "Mesa creada",
        });
        setShowTableModal(false);
      }
      loadRooms();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error al guardar";
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const deleteTable = async (table: Table) => {
    setConfirmDialog({
      show: true,
      title: "Eliminar mesa",
      message: `¿Eliminar la mesa "${table.name}"?`,
      onConfirm: async () => {
        setConfirmDialog({
          show: false,
          title: "",
          message: "",
          onConfirm: () => {},
        });
        try {
          const res = await authFetch(`${API_URL}/tables/${table.id}`, {
            method: "DELETE",
          });

          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.message || "Error");
          }

          setMessage({ type: "success", text: "Mesa eliminada" });
          loadRooms();
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Error al eliminar";
          setMessage({ type: "error", text: errorMessage });
        }
      },
    });
  };

  // Drag & Drop — pointer-based (useTableDrag hook)
  const handleTableMoveToAPI = useCallback(
    async (tableId: string, newX: number, newY: number) => {
      // Optimistic update in rooms state
      setRooms((prevRooms) =>
        prevRooms.map((room) => ({
          ...room,
          tables: room.tables.map((t) =>
            t.id === tableId ? { ...t, positionX: newX, positionY: newY } : t,
          ),
        })),
      );
      setSelectedRoom((prevRoom) =>
        prevRoom
          ? {
              ...prevRoom,
              tables: prevRoom.tables.map((t) =>
                t.id === tableId
                  ? { ...t, positionX: newX, positionY: newY }
                  : t,
              ),
            }
          : null,
      );
      try {
        // Sin Math.round: con snap desactivado (o cuando una smart guide
        // ajustó a una posición fraccionaria, p.ej. centrada entre dos
        // mesas de ancho impar) la posición real puede no ser un entero.
        // `positionX/Y` ya admiten decimales en BD (DOUBLE PRECISION) — solo
        // se recorta a 3 decimales para no guardar ruido de coma flotante,
        // de sobra para la precisión visual que puede distinguir el ojo.
        const res = await authFetch(`${API_URL}/tables/${tableId}`, {
          method: "PUT",
          body: JSON.stringify({
            positionX: roundPosition(newX),
            positionY: roundPosition(newY),
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          setMessage({
            type: "error",
            text: extractApiErrorMessage(
              data,
              "No se pudo guardar la posición de la mesa",
            ),
          });
          await loadRooms();
        }
      } catch (error) {
        console.error(error);
        setMessage({
          type: "error",
          text: "No se pudo guardar la posición de la mesa (error de red)",
        });
        await loadRooms();
      }
    },
    [API_URL, getAuthHeaders, loadRooms],
  );

  // Lista combinada mesas+objetos SOLO para que las smart guides de CADA
  // instancia del hook puedan alinear contra el otro tipo (una mesa se
  // alinea contra objetos decorativos y viceversa). No hay ambigüedad
  // sobre qué se arrastra: cada instancia solo INICIA el drag desde su
  // propio tipo de elemento (mesas vía `handleTableDragStart`, objetos vía
  // `handleDecorativeDragStart`), así que `draggingTableId`/`dragPositions`
  // de cada instancia nunca contienen ids del otro tipo.
  const dragAlignmentItems = useMemo(
    () => [...(selectedRoom?.tables ?? []), ...decorativeObjects],
    [selectedRoom?.tables, decorativeObjects],
  );

  const {
    draggingTableId,
    guides,
    selectedTableIds,
    getTablePosition: getTablePos,
    handleDragStart: handleTableDragStart,
    toggleSelect,
    clearSelection,
    alignTables,
  } = useTableDrag({
    tables: dragAlignmentItems,
    gridSize: CANVAS_CELLS,
    snapToGrid,
    snapThreshold: 6,
    editable: canEdit,
    containerRef: gridContainerRef,
    onTableMove: handleTableMoveToAPI,
  });

  // Drag & Drop de objetos decorativos — MISMO sistema pointer-based que
  // las mesas (useTableDrag), no HTML5 drag nativo (`draggable`/
  // `onDragStart`/`onDrop`): ese era el "fantasma" que reportó Luis — el
  // navegador pinta su propia imagen fantasma siguiendo el cursor mientras
  // el elemento real (y su estado en React) no se mueve hasta el evento
  // `drop`, así que se veían DOS cosas a la vez (el objeto quieto + el
  // fantasma nativo). No hizo falta tocar `useTableDrag.ts`: ya es
  // genérico (solo pide `{id, positionX, positionY, width, height}`, que
  // `DecorativeObject` ya cumple), así que se reutiliza tal cual con una
  // SEGUNDA instancia independiente — mismo mecanismo de snap/smart
  // guides/reposicionamiento en vivo que las mesas, pero con su propio
  // estado de arrastre/selección (los objetos siguen sin selección
  // múltiple: un clic abre su modal de edición, como siempre).
  const handleDecorativeMoveToAPI = useCallback(
    async (objectId: string, newX: number, newY: number) => {
      const posX = roundPosition(newX);
      const posY = roundPosition(newY);
      // Mismo fix de las 3 copias de estado que el resto de mutaciones de
      // objetos decorativos (decorativeObjects + rooms + selectedRoom) —
      // ver la nota del bug "el objeto salta" en map-editor.notas.md.
      const applyPosition = (o: DecorativeObject) =>
        o.id === objectId ? { ...o, positionX: posX, positionY: posY } : o;
      setDecorativeObjects((prev) => prev.map(applyPosition));
      setRooms((prevRooms) =>
        prevRooms.map((room) => ({
          ...room,
          decorativeObjects: room.decorativeObjects.map(applyPosition),
        })),
      );
      setSelectedRoom((prevRoom) =>
        prevRoom
          ? {
              ...prevRoom,
              decorativeObjects: prevRoom.decorativeObjects.map(applyPosition),
            }
          : null,
      );
      try {
        const res = await authFetch(
          `${API_URL}/decorative-objects/${objectId}`,
          {
            method: "PUT",
            body: JSON.stringify({
              positionX: posX,
              positionY: posY,
            }),
          },
        );
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          setMessage({
            type: "error",
            text: extractApiErrorMessage(
              data,
              "No se pudo guardar la posición del objeto decorativo",
            ),
          });
          await loadRooms();
        }
      } catch (error) {
        console.error(error);
        setMessage({
          type: "error",
          text: "No se pudo guardar la posición del objeto decorativo (error de red)",
        });
        await loadRooms();
      }
    },
    [API_URL, getAuthHeaders, loadRooms],
  );

  const {
    draggingTableId: draggingDecorativeId,
    guides: decorativeGuides,
    getTablePosition: getDecorativePos,
    handleDragStart: handleDecorativeDragStart,
  } = useTableDrag({
    tables: dragAlignmentItems,
    gridSize: CANVAS_CELLS,
    snapToGrid,
    snapThreshold: 6,
    editable: canEdit,
    containerRef: gridContainerRef,
    onTableMove: handleDecorativeMoveToAPI,
  });

  // "Acabo de arrastrar este objeto" — con un REF, no con el estado
  // `draggingDecorativeId`. El `click` nativo que sigue a soltar un
  // arrastre llega DESPUÉS de que React ya haya procesado el `mouseup` y
  // reseteado `draggingDecorativeId` a `null` (vía `setState`, asíncrono),
  // así que comprobar el estado dentro de `onClick` es una carrera que se
  // pierde casi siempre y el modal de edición se abría igualmente al
  // soltar. Un ref se lee de forma síncrona, sin ese problema. Umbral de
  // píxeles de pantalla (no celdas) para no bloquear el clic de un tap sin
  // apenas movimiento — mismo criterio que el pan táctil de fondo.
  const objectPointerDownRef = useRef<{
    id: string;
    x: number;
    y: number;
  } | null>(null);
  const justDraggedObjectIdRef = useRef<string | null>(null);

  const handleDecorativePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent, obj: DecorativeObject) => {
      // Staff (gating F6): el hook ya ignora el arrastre (`editable:
      // canEdit`), pero sin este guard el ref de "acabo de arrastrar" se
      // podía marcar igualmente con un mero temblor del puntero, comiéndose
      // el siguiente clic real que abre el modal de solo-lectura.
      if (!canEdit) return;
      const point = "touches" in e ? e.touches[0] : e;
      objectPointerDownRef.current = {
        id: obj.id,
        x: point.clientX,
        y: point.clientY,
      };
      handleDecorativeDragStart(e, obj);
    },
    [handleDecorativeDragStart, canEdit],
  );

  useEffect(() => {
    const handlePointerUp = (e: MouseEvent | TouchEvent) => {
      const start = objectPointerDownRef.current;
      objectPointerDownRef.current = null;
      if (!start) return;
      const point = "changedTouches" in e ? e.changedTouches[0] : e;
      if (!point) return;
      const dx = point.clientX - start.x;
      const dy = point.clientY - start.y;
      if (Math.hypot(dx, dy) >= DECORATIVE_DRAG_CLICK_THRESHOLD_PX) {
        // Movimiento real: el siguiente `click` sobre este objeto se
        // ignora una vez (se "consume" en el propio onClick).
        justDraggedObjectIdRef.current = start.id;
      }
    };
    window.addEventListener("mouseup", handlePointerUp);
    window.addEventListener("touchend", handlePointerUp);
    return () => {
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("touchend", handlePointerUp);
    };
  }, []);

  // Grid click to add table or clear selection
  const handleGridClick = (e: React.MouseEvent) => {
    if (!selectedRoom) return;
    const gridRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = Math.floor((e.clientX - gridRect.left) / viewport.cellPx);
    const y = Math.floor((e.clientY - gridRect.top) / viewport.cellPx);

    // Check if clicking on empty space
    const clickedTable = selectedRoom.tables.find((t) => {
      const pos = getTablePos(t);
      return (
        x >= pos.x && x < pos.x + t.width && y >= pos.y && y < pos.y + t.height
      );
    });

    if (!clickedTable) {
      clearSelection();
      // Staff (F6): clic en vacío puede seguir limpiando la selección, pero
      // no debe abrir el modal de alta de mesa (es una mutación).
      if (canEdit) {
        openTableModal(undefined, { x, y });
      }
    }
  };

  // Render table shape
  const renderTableShape = (table: Table, isGrid = true) => {
    const isSelected = selectedTableIds.includes(table.id);
    const isDragging = draggingTableId === table.id;
    const pos = getTablePos(table);
    const cellPct = 100 / CANVAS_CELLS;

    const baseClasses = `
      flex items-center justify-center font-normal overflow-hidden
      ${isSelected ? "bg-cyan-100 border-cyan-500 text-cyan-800 ring-2 ring-cyan-400" : table.isActive ? "bg-primary-100 border-primary-500 text-primary-800" : "bg-gray-100 border-gray-300 text-gray-500"}
      border cursor-pointer hover:shadow-lg hover:z-10 transition-shadow select-none
      ${isDragging ? "opacity-70 shadow-xl z-50" : ""}
    `;

    const shapeClasses = {
      rectangle: "rounded-sm",
      square: "rounded-sm",
      circle: "rounded-full",
    };

    if (isGrid) {
      return (
        <div
          key={table.id}
          onMouseDown={(e) => handleTableDragStart(e, table)}
          onTouchStart={(e) => handleTableDragStart(e, table)}
          onClick={(e) => {
            e.stopPropagation();
            if (isDragging) return;
            if (e.metaKey || e.ctrlKey) {
              toggleSelect(table.id, true);
            } else {
              toggleSelect(table.id, false);
            }
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            openTableModal(table);
          }}
          className={`absolute ${baseClasses} ${shapeClasses[table.shape]}`}
          style={{
            left: `calc(${pos.x * cellPct}% + 1px)`,
            top: `calc(${pos.y * cellPct}% + 1px)`,
            width: `calc(${(table.width || 1) * cellPct}% - 2px)`,
            height: `calc(${(table.height || 1) * cellPct}% - 2px)`,
          }}
          title={`${table.name} · ${table.minCapacity}-${table.maxCapacity} pers.`}
        >
          {(() => {
            // Tipografía escalada con el zoom (cellPx). El NOMBRE nunca se
            // recorta con "…": se envuelve en hasta 2 líneas (line-clamp-2)
            // en vez de truncarse a una — `fitNameToTwoLines` baja el
            // tamaño de fuente antes de dejar que el clamp recorte. Las
            // líneas secundarias siguen con la regla "cabe entera o no se
            // pinta" (`countFittingLines`).
            const capFontPx = clampNum(viewport.cellPx * 0.4, 7, 11);
            const boxWidthPx = (table.width || 1) * viewport.cellPx;
            const boxHeightPx = (table.height || 1) * viewport.cellPx;
            const { fontPx: nameFontPx, lines: nameLines } = fitNameToTwoLines(
              table.name,
              boxWidthPx,
              clampNum(viewport.cellPx * 0.5, 8, 14),
              8,
            );
            const lineCount = countFittingLines(boxHeightPx, [
              ...Array(nameLines).fill(nameFontPx),
              capFontPx,
            ]);
            return (
              <div className="text-center leading-none px-0.5 max-w-full">
                {lineCount >= 1 && (
                  <div
                    className="font-normal break-words line-clamp-2"
                    style={{ fontSize: nameFontPx }}
                  >
                    {table.name}
                  </div>
                )}
                {lineCount >= 1 + nameLines && (
                  <div
                    className="opacity-75 truncate"
                    style={{ fontSize: capFontPx }}
                  >
                    {table.minCapacity}-{table.maxCapacity}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      );
    }

    return (
      <div
        key={table.id}
        onClick={() => openTableModal(table)}
        className={`${baseClasses} ${shapeClasses[table.shape]} p-3 min-w-[100px]`}
      >
        <div className="text-center">
          <div className="text-sm font-normal">{table.name}</div>
          <div className="text-xs opacity-75">
            {table.minCapacity}-{table.maxCapacity} personas
          </div>
        </div>
      </div>
    );
  };

  // Render decorative icon. `className` por defecto pensado para tarjetas
  // normales (legend/lista/modal); el canvas (celdas de 18px) pasa un
  // tamaño más pequeño para que quepa en la celda.
  const getDecorativeIcon = (type: DecorativeType, className = "w-6 h-6") => {
    switch (type) {
      case "bar":
        return <Coffee className={className} />;
      case "bathroom":
        return <Bath className={className} />;
      case "door":
        return <DoorOpen className={className} />;
      case "wall":
        return <RectangleHorizontal className={className} />;
      case "plant":
        return <Flower2 className={className} />;
      case "column":
        return <Columns className={className} />;
      default:
        return null;
    }
  };

  // Render decorative object
  const renderDecorativeObject = (obj: DecorativeObject) => {
    const typeConfig = DECORATIVE_TYPES.find((t) => t.type === obj.type);
    const bgColor = typeConfig?.color || "bg-gray-500";
    const cellPct = 100 / CANVAS_CELLS;
    const isDraggingObject = draggingDecorativeId === obj.id;
    const pos = getDecorativePos(obj);

    const isVertical = obj.height > obj.width;

    return (
      <div
        key={obj.id}
        onMouseDown={(e) => handleDecorativePointerDown(e, obj)}
        onTouchStart={(e) => handleDecorativePointerDown(e, obj)}
        onClick={(e) => {
          e.stopPropagation();
          if (isDraggingObject) return;
          if (justDraggedObjectIdRef.current === obj.id) {
            // Se acaba de soltar un arrastre real sobre este objeto:
            // se consume el aviso y no se abre el modal esta vez.
            justDraggedObjectIdRef.current = null;
            return;
          }
          openDecorativeModal(obj);
        }}
        className={`group absolute ${bgColor} text-white rounded-sm flex flex-col items-center justify-center cursor-grab active:cursor-grabbing hover:opacity-90 hover:z-10 transition-opacity shadow-sm border border-white/30 overflow-hidden select-none ${
          isDraggingObject ? "opacity-70 shadow-xl z-50" : ""
        }`}
        style={{
          left: `calc(${pos.x * cellPct}% + 1px)`,
          top: `calc(${pos.y * cellPct}% + 1px)`,
          width: `calc(${obj.width * cellPct}% - 2px)`,
          height: `calc(${obj.height * cellPct}% - 2px)`,
          transform: `rotate(${obj.rotation}deg)`,
        }}
        title={obj.label || typeConfig?.label}
      >
        {/* Botón rápido de eliminar: visible solo al pasar el ratón (no
            hace falta abrir el modal para un borrado rápido). Reutiliza
            `deleteDecorativeObject`, que ya pide confirmación con el
            `confirmDialog` propio (no window.confirm). Oculto para staff
            (F6: es una mutación). */}
        {canEdit && (
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              deleteDecorativeObject(obj);
            }}
            className="absolute top-0 right-0 w-3.5 h-3.5 flex items-center justify-center bg-black/40 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity z-20"
            title="Eliminar objeto"
          >
            <X className="w-2.5 h-2.5" />
          </button>
        )}
        {getDecorativeIcon(obj.type, "w-3.5 h-3.5")}
        {obj.label && (
          <span
            className="text-[8px] font-medium leading-none mt-0.5 truncate max-w-full px-0.5"
            style={
              isVertical
                ? { writingMode: "vertical-rl", textOrientation: "mixed" }
                : undefined
            }
          >
            {obj.label}
          </span>
        )}
      </div>
    );
  };

  // Decorative modal
  // Centro (en celdas) de la parte del lienzo actualmente visible dentro
  // del recuadro (según scroll + zoom) — para que un objeto nuevo aparezca
  // donde el usuario está mirando, no en una esquina fija que obligue a
  // hacer scroll para encontrarlo.
  const getViewportCenterCell = useCallback(
    (width: number, height: number): { x: number; y: number } => {
      const container = viewport.containerRef.current;
      if (!container) return { x: 0, y: 0 };
      const centerXCell =
        (container.scrollLeft + container.clientWidth / 2) / viewport.cellPx;
      const centerYCell =
        (container.scrollTop + container.clientHeight / 2) / viewport.cellPx;
      return {
        x: clampNum(Math.round(centerXCell - width / 2), 0, CANVAS_CELLS - width),
        y: clampNum(Math.round(centerYCell - height / 2), 0, CANVAS_CELLS - height),
      };
    },
    [viewport.containerRef, viewport.cellPx],
  );

  // Botón de cabecera "Añadir Mesa": a diferencia de "Guardar y siguiente"
  // (que ancla la búsqueda a la mesa recién guardada), aquí no hay ningún
  // punto de partida "natural" — sin esto, `openTableModal()` dejaba
  // positionX/Y en 0 y la mesa nueva podía quedar solapada con otra o fuera
  // de la parte visible del lienzo. Se ancla la búsqueda de hueco libre al
  // centro de lo que el usuario está mirando ahora mismo (mismo
  // `getViewportCenterCell` que usan los objetos decorativos) y se evita
  // solapar con las mesas ya existentes (`findFreePosition`, igual que
  // "Guardar y siguiente").
  const openTableModalAtFreePosition = () => {
    if (!selectedRoom) {
      openTableModal();
      return;
    }
    const { width, height } = STANDARD_TABLE_SIZE.rectangle;
    const occupiedRects: Rect[] = (selectedRoom.tables || []).map((t) => ({
      x: t.positionX,
      y: t.positionY,
      width: t.width,
      height: t.height,
    }));
    const start = getViewportCenterCell(width, height);
    const position = findFreePosition(
      CANVAS_CELLS,
      width,
      height,
      occupiedRects,
      start.x,
      start.y,
    );
    openTableModal(undefined, position);
  };

  const openDecorativeModal = (
    obj?: DecorativeObject,
    position?: { x: number; y: number },
  ) => {
    if (obj) {
      setEditingDecorative(obj);
      setDecorativeForm({
        type: obj.type,
        label: obj.label || "",
        positionX: obj.positionX,
        positionY: obj.positionY,
        width: obj.width,
        height: obj.height,
        rotation: obj.rotation,
      });
    } else {
      setEditingDecorative(null);
      const defaultWidth = 2;
      const defaultHeight = 1;
      const center =
        position ?? getViewportCenterCell(defaultWidth, defaultHeight);
      setDecorativeForm({
        type: "bar",
        label: "",
        positionX: center.x,
        positionY: center.y,
        width: defaultWidth,
        height: defaultHeight,
        rotation: 0,
      });
    }
    setShowDecorativeModal(true);
  };

  const saveDecorativeObject = async () => {
    if (!selectedRoom) return;

    setSaving(true);
    try {
      if (editingDecorative) {
        // Update existing object via API
        const res = await authFetch(
          `${API_URL}/decorative-objects/${editingDecorative.id}`,
          {
            method: "PUT",
            body: JSON.stringify({
              type: decorativeForm.type,
              label: decorativeForm.label || null,
              positionX: decorativeForm.positionX,
              positionY: decorativeForm.positionY,
              width: decorativeForm.width,
              height: decorativeForm.height,
              rotation: decorativeForm.rotation,
            }),
          },
        );
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(
            extractApiErrorMessage(data, "Error al actualizar el objeto"),
          );
        }
      } else {
        // Create new object via API
        const res = await authFetch(`${API_URL}/decorative-objects`, {
          method: "POST",
          body: JSON.stringify({
            roomId: selectedRoom.id,
            type: decorativeForm.type,
            label: decorativeForm.label || null,
            positionX: decorativeForm.positionX,
            positionY: decorativeForm.positionY,
            width: decorativeForm.width,
            height: decorativeForm.height,
            rotation: decorativeForm.rotation,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(
            extractApiErrorMessage(data, "Error al crear el objeto"),
          );
        }
      }

      await loadRooms(); // Reload to get updated data
      setShowDecorativeModal(false);
      setMessage({
        type: "success",
        text: editingDecorative ? "Objeto actualizado" : "Objeto añadido",
      });
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : "Error al guardar el objeto";
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const deleteDecorativeObject = (obj: DecorativeObject) => {
    if (!selectedRoom) return;

    setConfirmDialog({
      show: true,
      title: "Eliminar objeto decorativo",
      message: "¿Eliminar este objeto decorativo?",
      onConfirm: async () => {
        setConfirmDialog({
          show: false,
          title: "",
          message: "",
          onConfirm: () => {},
        });

        try {
          const res = await authFetch(
            `${API_URL}/decorative-objects/${obj.id}`,
            {
              method: "DELETE",
            },
          );
          if (!res.ok) {
            const data = await res.json().catch(() => null);
            throw new Error(
              extractApiErrorMessage(data, "Error al eliminar el objeto"),
            );
          }

          await loadRooms(); // Reload to get updated data
          setMessage({ type: "success", text: "Objeto eliminado" });
        } catch (error) {
          console.error(error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Error al eliminar el objeto";
          setMessage({ type: "error", text: errorMessage });
        }
      },
    });
  };


  // F1: en modo citas el mapa de mesas no aplica (los "salones" de citas son
  // otro modelo, gestionado en otra pantalla). El sidebar ya oculta el
  // enlace; esto cubre el acceso por URL directa. AFTER all hooks (no
  // rompe las reglas de hooks).
  if (currentBookingMode === "appointments") {
    return <Navigate to="/" replace />;
  }

  // Subscription loading check - AFTER all hooks are declared
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  // Feature access check - AFTER all hooks are declared
  if (!canAccessTableMaps && features) {
    return (
      <UpgradeRequired feature="tableMaps" currentPlan={features.planName} />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Mapa de Mesas
          </h1>
          <p className="text-gray-500 text-sm md:text-base">
            Gestiona las salas y mesas de tu restaurante
          </p>
        </div>
        <button
          onClick={() => openRoomModal()}
          disabled={!canEdit}
          className="flex items-center gap-2 px-3 md:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 md:w-5 h-4 md:h-5" />
          Nueva Sala
        </button>
      </div>

      {/* Nota de solo-lectura para staff (F6): el backend restringe estas
          mutaciones a owner/admin; se avisa aquí en vez de dejar que el
          usuario descubra los 403 botón a botón. */}
      {!canEdit && (
        <div className="p-2.5 md:p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs md:text-sm text-gray-500 flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Solo owner/admin pueden editar la estructura de mesas. Puedes
            consultar el mapa, pero no crear, mover ni eliminar salas, mesas
            u objetos decorativos.
          </span>
        </div>
      )}

      {/* Message */}
      {message && (
        <div
          className={`p-3 md:p-4 rounded-lg text-sm md:text-base ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      {loadError ? (
        // F4: error de CARGA distinto de "no hay salas" (rooms=[] real) —
        // sin esto, un fallo de red/permiso dejaba `rooms=[]` y se pintaba
        // igualmente el estado vacío feliz "Crear Primera Sala", que
        // engañaba (parecía que el restaurante no tenía salas, no que la
        // carga había fallado).
        <div className="bg-white rounded-lg shadow p-8 md:p-12 text-center border border-red-100">
          <LayoutGrid className="w-12 md:w-16 h-12 md:h-16 mx-auto text-red-300 mb-4" />
          <h2 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">
            No se pudieron cargar las salas
          </h2>
          <p className="text-gray-500 mb-6 text-sm md:text-base">
            {loadError}
          </p>
          <button
            onClick={() => {
              setLoading(true);
              loadRooms();
            }}
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm md:text-base"
          >
            <RefreshCw className="w-4 md:w-5 h-4 md:h-5" />
            Reintentar
          </button>
        </div>
      ) : rooms.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 md:p-12 text-center">
          <LayoutGrid className="w-12 md:w-16 h-12 md:h-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">
            No hay salas configuradas
          </h2>
          <p className="text-gray-500 mb-6 text-sm md:text-base">
            Crea tu primera sala para empezar a añadir mesas
          </p>
          <button
            onClick={() => openRoomModal()}
            disabled={!canEdit}
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 md:w-5 h-4 md:h-5" />
            Crear Primera Sala
          </button>
        </div>
      ) : (
        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 md:gap-6">
          {/* Room List - Horizontal scroll on mobile */}
          <div className="lg:col-span-1 space-y-3 md:space-y-4">
            <h2 className="font-semibold text-gray-900 text-sm md:text-base">
              Salas
            </h2>
            <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
              {rooms.map((room, index) => (
                <div
                  key={room.id}
                  onClick={() => setSelectedRoom(room)}
                  className={`p-3 md:p-4 rounded-lg border-2 cursor-pointer transition-all flex-shrink-0 lg:flex-shrink min-w-[150px] lg:min-w-0 ${
                    selectedRoom?.id === room.id
                      ? "border-primary-500 bg-primary-50"
                      : room.isActive
                        ? "border-gray-200 bg-white hover:border-gray-300"
                        : "border-gray-200 bg-gray-100 hover:border-gray-300 opacity-60"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h3
                          className={`font-medium ${room.isActive ? "text-gray-900" : "text-gray-500"}`}
                        >
                          {room.name}
                        </h3>
                        {room.isDefault && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                        {!room.isActive && (
                          <span className="text-xs text-gray-400">
                            (oculta)
                          </span>
                        )}
                        {room.isActive &&
                          (room.hiddenChannels?.length ?? 0) > 0 && (
                            <span
                              className="text-xs text-amber-600"
                              title={`No visible en: ${ROOM_CHANNELS.filter(
                                (c) => room.hiddenChannels.includes(c.id),
                              )
                                .map((c) => c.label)
                                .join(", ")}`}
                            >
                              (canales limitados)
                            </span>
                          )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {room.tables.length} mesas
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {/* Reordenar (F5): sube/baja la sala e intercambia su
                          `sortOrder` vía PUT /tables/rooms/reorder */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveRoom(room, "up");
                        }}
                        disabled={!canEdit || reorderingRooms || index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                        title="Subir"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveRoom(room, "down");
                        }}
                        disabled={
                          !canEdit ||
                          reorderingRooms ||
                          index === rooms.length - 1
                        }
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                        title="Bajar"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRoomVisibility(room);
                        }}
                        disabled={!canEdit}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                        title={
                          room.isActive
                            ? "Ocultar de todos los canales"
                            : "Mostrar en los canales marcados"
                        }
                      >
                        {room.isActive ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                      {!room.isDefault && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRoomAsDefault(room);
                          }}
                          disabled={!canEdit}
                          className="p-1 text-gray-400 hover:text-yellow-500 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                          title="Marcar como por defecto"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openRoomModal(room);
                        }}
                        disabled={!canEdit}
                        className="p-1 text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteRoom(room);
                        }}
                        disabled={!canEdit}
                        className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grid Map */}
          <div className="lg:col-span-3">
            {selectedRoom ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-semibold text-gray-900">
                    {selectedRoom.name}
                    <span className="text-gray-400 font-normal ml-2">
                      ({selectedRoom.tables.length} mesas)
                    </span>
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openDecorativeModal()}
                      disabled={!canEdit}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Coffee className="w-4 h-4" />
                      Objeto Decorativo
                    </button>
                    <button
                      onClick={openTableModalAtFreePosition}
                      disabled={!canEdit}
                      className="flex items-center gap-2 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                      Añadir Mesa
                    </button>
                  </div>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                  Haz clic en un espacio vacío para añadir una mesa. Arrastra
                  las mesas y objetos para reposicionarlos. Desplázate por el
                  lienzo con scroll o arrastrando con el botón central del
                  ratón.
                </p>

                {/* Decorative objects legend */}
                {decorativeObjects.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {DECORATIVE_TYPES.map((dt) => {
                      const count = decorativeObjects.filter(
                        (o) => o.type === dt.type,
                      ).length;
                      if (count === 0) return null;
                      return (
                        <span
                          key={dt.type}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${dt.color} text-white`}
                        >
                          {dt.label}: {count}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Toolbar: alignment + snap toggle */}
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {canEdit && (
                    <AlignmentToolbar
                      selectedCount={selectedTableIds.length}
                      onAlign={alignTables}
                    />
                  )}
                  <button
                    onClick={() => setSnapToGrid((v) => !v)}
                    className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                      snapToGrid
                        ? "bg-cyan-50 border-cyan-300 text-cyan-700"
                        : "bg-gray-50 border-gray-200 text-gray-500"
                    }`}
                    title={
                      snapToGrid
                        ? "Snap al grid activado"
                        : "Snap al grid desactivado"
                    }
                  >
                    <Grid3X3 className="w-3.5 h-3.5" />
                    Snap
                  </button>
                  {selectedTableIds.length > 0 && (
                    <span className="text-xs text-gray-400">
                      {selectedTableIds.length} mesa
                      {selectedTableIds.length > 1 ? "s" : ""} · Ctrl+clic para
                      multi-selección · Doble clic para editar
                    </span>
                  )}
                </div>

                {/* Recuadro visible: ancho SIEMPRE al 100% disponible
                    (`w-full`, tope `maxWidth` en pantallas grandes) y alto
                    determinista pero responsive (`min(540px, 65vh)` — nunca
                    vh/flex a pelo, pero tampoco un alto fijo que no quepa en
                    pantallas bajas/móviles). Dentro se panea el lienzo real
                    de 50x50 celdas a `viewport.cellPx` px/celda EXACTOS
                    (sin transform/scale — la geometría de la rejilla no se
                    toca en ningún viewport, solo cambia cuánto lienzo entra
                    en el recuadro). Zoom dinámico: auto-encuadre + lupa
                    manual + vista guardada por sala. */}
                <div
                  className="relative w-full"
                  style={{ maxWidth: RECUADRO_PX }}
                >
                  <div
                    ref={viewport.containerRef}
                    onMouseDown={viewport.onMouseDown}
                    onTouchStart={viewport.onTouchStart}
                    onScroll={viewport.onScroll}
                    className={`w-full overflow-auto border-2 border-dashed border-gray-300 rounded-lg bg-gray-100 ${
                      viewport.isPanning ? "cursor-grabbing" : ""
                    }`}
                    style={{ height: "min(540px, 65vh)" }}
                  >
                    <div
                      ref={gridContainerRef}
                      className="relative bg-gray-50 select-none"
                      style={{
                        width: CANVAS_CELLS * viewport.cellPx,
                        height: CANVAS_CELLS * viewport.cellPx,
                      }}
                      onClick={handleGridClick}
                    >
                    {/* Grid lines */}
                    <div className="absolute inset-0 pointer-events-none">
                      {Array.from({ length: CANVAS_CELLS }).map((_, i) => (
                        <div
                          key={`v-${i}`}
                          className="absolute top-0 bottom-0 border-l border-gray-200"
                          style={{ left: `${(i / CANVAS_CELLS) * 100}%` }}
                        />
                      ))}
                      {Array.from({ length: CANVAS_CELLS }).map((_, i) => (
                        <div
                          key={`h-${i}`}
                          className="absolute left-0 right-0 border-t border-gray-200"
                          style={{ top: `${(i / CANVAS_CELLS) * 100}%` }}
                        />
                      ))}
                    </div>

                    {/* Alignment guides (arrastre de mesas) */}
                    {guides.map((guide, i) => (
                      <div
                        key={`guide-${i}`}
                        className="absolute pointer-events-none z-40"
                        style={
                          guide.type === "vertical"
                            ? {
                                left: `${guide.position}%`,
                                top: 0,
                                bottom: 0,
                                width: 1,
                                borderLeft: "1px dashed #06b6d4",
                              }
                            : {
                                top: `${guide.position}%`,
                                left: 0,
                                right: 0,
                                height: 1,
                                borderTop: "1px dashed #06b6d4",
                              }
                        }
                      />
                    ))}

                    {/* Alignment guides (arrastre de objetos decorativos) */}
                    {decorativeGuides.map((guide, i) => (
                      <div
                        key={`dguide-${i}`}
                        className="absolute pointer-events-none z-40"
                        style={
                          guide.type === "vertical"
                            ? {
                                left: `${guide.position}%`,
                                top: 0,
                                bottom: 0,
                                width: 1,
                                borderLeft: "1px dashed #06b6d4",
                              }
                            : {
                                top: `${guide.position}%`,
                                left: 0,
                                right: 0,
                                height: 1,
                                borderTop: "1px dashed #06b6d4",
                              }
                        }
                      />
                    ))}

                    {/* Decorative Objects (behind tables) */}
                    {decorativeObjects.map((obj) =>
                      renderDecorativeObject(obj),
                    )}

                    {/* Tables */}
                    {selectedRoom.tables.map((table) =>
                      renderTableShape(table, true),
                    )}
                    </div>
                  </div>
                  <ZoomControls
                    onZoomIn={viewport.zoomIn}
                    onZoomOut={viewport.zoomOut}
                    onFit={() =>
                      viewport.fitToContent(
                        computeContentBBox(
                          selectedRoom.tables ?? [],
                          selectedRoom.decorativeObjects ?? [],
                        ),
                      )
                    }
                  />
                </div>

                {/* Decorative Objects List */}
                {decorativeObjects.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-medium text-gray-700 mb-3">
                      Objetos Decorativos
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {decorativeObjects.map((obj) => {
                        const typeConfig = DECORATIVE_TYPES.find(
                          (t) => t.type === obj.type,
                        );
                        return (
                          <div
                            key={obj.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={`p-1 rounded ${typeConfig?.color || "bg-gray-500"} text-white`}
                              >
                                {getDecorativeIcon(obj.type)}
                              </div>
                              <div>
                                <div className="font-medium text-sm">
                                  {typeConfig?.label}
                                </div>
                                {obj.label && (
                                  <div className="text-xs text-gray-500">
                                    {obj.label}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => openDecorativeModal(obj)}
                                className="p-1 text-gray-400 hover:text-primary-600"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteDecorativeObject(obj)}
                                disabled={!canEdit}
                                className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Table List */}
                {selectedRoom.tables.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-medium text-gray-700 mb-3">
                      Lista de Mesas
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {selectedRoom.tables.map((table) => (
                        <div
                          key={table.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                        >
                          <div className="flex items-center gap-2">
                            <Armchair className="w-5 h-5 text-gray-400" />
                            <div>
                              <div className="font-medium text-sm">
                                {table.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {table.minCapacity}-{table.maxCapacity} pers.
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => openTableModal(table)}
                              className="p-1 text-gray-400 hover:text-primary-600"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteTable(table)}
                              disabled={!canEdit}
                              className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <Armchair className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  Selecciona una sala para ver sus mesas
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Room Modal */}
      {showRoomModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {editingRoom ? "Editar Sala" : "Nueva Sala"}
              </h2>
              <button onClick={() => setShowRoomModal(false)}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={roomForm.name}
                  onChange={(e) =>
                    setRoomForm({ ...roomForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Ej: Terraza, Salón Principal..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={roomForm.description}
                  onChange={(e) =>
                    setRoomForm({ ...roomForm, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  placeholder="Descripción opcional..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visible en
                </label>
                <div className="space-y-1.5 border rounded-lg p-3">
                  {ROOM_CHANNELS.map((channel) => (
                    <label
                      key={channel.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={!roomForm.hiddenChannels.includes(channel.id)}
                        onChange={() => toggleRoomChannel(channel.id)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">
                        {channel.label}
                        <span className="text-gray-400"> — {channel.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Marca los canales donde esta sala acepta reservas. Sin
                  ninguno marcado, la sala no se ofrece en ningún canal.
                </p>
                {editingRoom && !editingRoom.isActive && (
                  <p className="mt-1 text-xs text-amber-600">
                    Esta sala está oculta de todos los canales (ojo tachado en
                    la lista). Los canales marcados se aplicarán cuando vuelva
                    a mostrarse.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowRoomModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={saveRoom}
                disabled={saving || !canEdit}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Modal */}
      {showTableModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canEdit) {
              e.preventDefault();
              saveTable(true);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90dvh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {editingTable ? "Editar Mesa" : "Nueva Mesa"}
              </h2>
              <button onClick={() => setShowTableModal(false)}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Error message inside modal */}
            {message && message.type === "error" && (
              <div className="p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200">
                {message.text}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={tableForm.name}
                  onChange={(e) =>
                    setTableForm({ ...tableForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Ej: Mesa 1, VIP 1..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacidad Mínima
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={tableForm.minCapacity}
                    onChange={(e) =>
                      handleCapacityInputChange("minCapacity", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacidad Máxima
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={tableForm.maxCapacity}
                    onChange={(e) =>
                      handleCapacityInputChange("maxCapacity", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Forma
                </label>
                <div className="flex gap-4">
                  {[
                    {
                      value: "rectangle" as const,
                      icon: Square,
                      label: "Rectangular",
                    },
                    {
                      value: "square" as const,
                      icon: Square,
                      label: "Cuadrada",
                    },
                    {
                      value: "circle" as const,
                      icon: Circle,
                      label: "Redonda",
                    },
                  ].map(({ value, icon: Icon, label }) => (
                    <button
                      key={value}
                      onClick={() => {
                        // Tamaño estándar de la forma al cambiarla: siempre
                        // en modo creación (no hay `editingTable`), y en
                        // modo edición SOLO si la mesa está en 1×1 (el caso
                        // "muy pequeña" que se quiere corregir) — si ya
                        // tiene un tamaño a medida, cambiar de forma no lo
                        // pisa.
                        const isNewTable = !editingTable;
                        const isTiny =
                          tableForm.width === 1 && tableForm.height === 1;
                        setTableForm({
                          ...tableForm,
                          shape: value,
                          ...(isNewTable || isTiny
                            ? STANDARD_TABLE_SIZE[value]
                            : null),
                        });
                      }}
                      className={`flex-1 flex flex-col items-center gap-2 p-3 border-2 rounded-lg transition-colors ${
                        tableForm.shape === value
                          ? "border-primary-500 bg-primary-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="text-xs">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ancho (celdas)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={tableForm.width}
                    onChange={(e) =>
                      setTableForm({
                        ...tableForm,
                        width: clampNum(parseInt(e.target.value) || 1, 1, 10),
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alto (celdas)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={tableForm.height}
                    onChange={(e) =>
                      setTableForm({
                        ...tableForm,
                        height: clampNum(parseInt(e.target.value) || 1, 1, 10),
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridad
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={tableForm.priority}
                  onChange={(e) =>
                    setTableForm({
                      ...tableForm,
                      priority: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Las mesas con mayor prioridad se asignan primero
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              {editingTable && (
                <button
                  onClick={() => {
                    setShowTableModal(false);
                    deleteTable(editingTable);
                  }}
                  disabled={saving || !canEdit}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </button>
              )}
              <button
                onClick={() => setShowTableModal(false)}
                className="flex-1 min-w-[100px] px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => saveTable(true)}
                disabled={saving || !canEdit}
                title="Guarda esta mesa y prepara la siguiente con la misma configuración (⌘/Ctrl + Intro)"
                className="flex-1 min-w-[170px] flex items-center justify-center gap-2 px-4 py-2 border-2 border-primary-600 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Guardar y siguiente
              </button>
              <button
                onClick={() => saveTable()}
                disabled={saving || !canEdit}
                className="flex-1 min-w-[110px] flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar
              </button>
            </div>
            {!canEdit && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                Solo owner/admin pueden editar la estructura de mesas.
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2 text-center">
              ⌘/Ctrl + Intro: guardar y crear la siguiente
            </p>
          </div>
        </div>
      )}

      {/* Decorative Object Modal */}
      {showDecorativeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingDecorative
                  ? "Editar Objeto"
                  : "Nuevo Objeto Decorativo"}
              </h3>
              <button
                onClick={() => setShowDecorativeModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {DECORATIVE_TYPES.map((dt) => (
                    <button
                      key={dt.type}
                      type="button"
                      onClick={() =>
                        setDecorativeForm({ ...decorativeForm, type: dt.type })
                      }
                      className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                        decorativeForm.type === dt.type
                          ? `${dt.color} text-white border-transparent`
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {getDecorativeIcon(dt.type)}
                      <span className="text-xs mt-1">{dt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Etiqueta (opcional)
                </label>
                <input
                  type="text"
                  value={decorativeForm.label}
                  onChange={(e) =>
                    setDecorativeForm({
                      ...decorativeForm,
                      label: e.target.value,
                    })
                  }
                  placeholder="Ej: Barra principal, Baños señoras..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ancho (celdas)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={decorativeForm.width}
                    onChange={(e) =>
                      setDecorativeForm({
                        ...decorativeForm,
                        width: clampNum(parseInt(e.target.value) || 1, 1, 20),
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alto (celdas)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={decorativeForm.height}
                    onChange={(e) =>
                      setDecorativeForm({
                        ...decorativeForm,
                        height: clampNum(parseInt(e.target.value) || 1, 1, 20),
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Posición X
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={CANVAS_CELLS - 1}
                    value={decorativeForm.positionX}
                    onChange={(e) =>
                      setDecorativeForm({
                        ...decorativeForm,
                        positionX: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Posición Y
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={CANVAS_CELLS - 1}
                    value={decorativeForm.positionY}
                    onChange={(e) =>
                      setDecorativeForm({
                        ...decorativeForm,
                        positionY: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rotación
                </label>
                <div className="flex gap-2">
                  {[0, 90, 180, 270].map((deg) => (
                    <button
                      key={deg}
                      type="button"
                      onClick={() =>
                        setDecorativeForm({ ...decorativeForm, rotation: deg })
                      }
                      className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        decorativeForm.rotation === deg
                          ? "bg-primary-100 border-primary-500 text-primary-700"
                          : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {deg}°
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDecorativeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={saveDecorativeObject}
                disabled={!canEdit}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {confirmDialog.title}
              </h3>
              <p className="text-gray-600">{confirmDialog.message}</p>
            </div>
            <div className="flex gap-3 p-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() =>
                  setConfirmDialog({
                    show: false,
                    title: "",
                    message: "",
                    onConfirm: () => {},
                  })
                }
                className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
